import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CheckAll as CheckAllIcon,
  OpenInNew as OpenInNewIcon,
} from "@actinc/dls/icons";
import _ from "lodash/fp";
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Assignment,
  LabelImportant,
  LabelImportantOutlined,
} from "@mui/icons-material";

import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";

import { Backdrop, DataGrid, PreviewContent } from "../../../components";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";

import { setState } from "../../../services/sessionState";
import { useSessionStore } from "../../../services/hooks";
import {
  dueDateTransform,
  getSelectOptionsFromMetadata,
  getTaskOptions,
  getUserOptions,
  getWorkspaceURIFromTask,
  PREVIEW_TYPES,
  setTitle,
} from "../../../services/utils";
import { CompleteTasksDialog } from "./CompleteTasksDialog/";
import { CompareReferencePassageDialog } from "../../../components/CompareReferencePassageDialog/";
import { MissingRequirementsDialog } from "./MissingRequirementsDialog";
import { PreviewContentDialog } from "./PreviewContentDialog";
import { SelectUserDialog } from "../../../components/SelectUserDialog";
import { SelectUserGroupsDialog } from "./SelectUserGroupsDialog";

const READY_FILTERS = {
  ALL: "ALL",
  NOT_READY: "NOT_READY",
  READY: "READY",
};

const READY_FILTER_OPTIONS = [
  { label: "All", value: READY_FILTERS.ALL },
  { label: "Ready", value: READY_FILTERS.READY },
  { label: "Not Ready", value: READY_FILTERS.NOT_READY },
];

const UserTasks = () => {
  const datagridKey = "userTasks";
  const [datagridModels = {}] = useSessionStore("datagrid");

  const page = useRef(0);
  const pagesToNextCursors = useRef([]);

  const location = useLocation();
  setState("lastSearchLocation")(location.pathname);
  const navigate = useNavigate();

  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);

  const [, completeTasks] = useMutation(completeTasksMutation);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [
    compareReferencePassageDialogProps,
    setCompareReferencePassageDialogProps,
  ] = useState({
    open: false,
  });
  const [completeTasksDialogProps, setCompleteTasksDialogProps] = useState({
    open: false,
  });
  const [missingRequirementsDialogProps, setMissingRequirementsDialogProps] =
    useState({
      open: false,
    });
  const [previewContentDialogProps, setPreviewContentDialogProps] = useState({
    open: false,
  });
  const [selectUserDialogProps, setSelectUserDialogProps] = useState({
    open: false,
  });
  const [selectUserGroupsDialogProps, setSelectUserGroupsDialogProps] =
    useState({
      open: false,
    });
  const [readyFilter, setReadyFilter] = useState(READY_FILTERS.ALL);

  const [getTasksResponse, refreshTasksByUser] = useQuery({
    query: getTasksByUserQuery,
    pause:
      !datagridModels[datagridKey]?.filterModel?.items.every(
        (filter) =>
          ["isEmpty", "isNotEmpty"].includes(filter.operator) ||
          (typeof filter.value !== "undefined" &&
            !(Array.isArray(filter.value) && filter.value.length === 0)),
      ) ||
      !paginationModel.pageSize ||
      (page.current !== 0 && !pagesToNextCursors.current[page.current - 1]) ||
      !datagridModels[datagridKey]?.sortModel,
    variables: {
      filterModel: {
        items: datagridModels[datagridKey]?.filterModel.items.map(
          ({ field, id, operator, value }) => ({
            field,
            id,
            operator,
            value:
              value instanceof Date
                ? value.toISOString()
                : typeof value === "object"
                  ? JSON.stringify(value)
                  : value,
          }),
        ),
        logicOperator: datagridModels[datagridKey]?.filterModel.logicOperator,
      },
      paginationModel: {
        cursor: pagesToNextCursors.current[page.current - 1],
        pageSize: paginationModel.pageSize,
      },
      readyFilter,
      sortModel: datagridModels[datagridKey]?.sortModel,
    },
  });

  const { data, error, fetching } = getTasksResponse;

  const rows = useMemo(() => {
    return data?.tasksByUser?.rows || [];
  }, [data?.tasksByUser?.rows]);

  const taskNameOptions = useMemo(() => getTaskOptions(), []);

  const contentAreaOptions = useMemo(() => {
    const itemOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("contentArea");
    const passageOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("contentArea");
    return [...itemOptions, ...passageOptions]
      .map(({ stringValue }) => stringValue)
      .filter((value, index, array) => array.indexOf(value) === index)
      .sort();
  }, [metadataConfiguration, programs]);

  const developmentStateOptions = useMemo(() => {
    const itemOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("developmentState");
    const passageOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
      programs,
    )("item")("developmentState");
    return [...itemOptions, ...passageOptions]
      .map(({ stringValue }) => stringValue)
      .filter((value, index, array) => array.indexOf(value) === index)
      .sort();
  }, [metadataConfiguration, programs]);

  const selectedRows = useMemo(
    () => rows.filter((row) => rowSelectionModel.includes(row.id)),
    [rows, rowSelectionModel],
  );

  useEffect(() => {
    if (
      !data?.tasksByUser.pageInfo.nextCursor ||
      pagesToNextCursors.current[page.current]
    )
      return;
    pagesToNextCursors.current[page.current] =
      data.tasksByUser.pageInfo.nextCursor;
  }, [data?.tasksByUser.pageInfo.nextCursor]);

  useEffect(() => {
    if (getTasksResponse.stale) {
      refreshTasksByUser();
    }
  }, [getTasksResponse.stale, refreshTasksByUser]);

  useEffect(() => {
    setTitle(["CDE", "Dashboard", "Tasks"]);
  }, []);

  const handleCompleteTasksDialogClose =
    (taskIDs, selected, prerequisites) => (value) => {
      if (
        selected[0].taskDefinition.key === "Final Review" &&
        value.buttonLabel === "Submit" &&
        selected.some(
          (task) =>
            task.item?.hasUnresolvedChangeTags ||
            task.passage?.hasUnresolvedChangeTags,
        )
      ) {
        return openMissingRequirementsDialog({ prerequisites, selected });
      }

      setCompleteTasksDialogProps({ open: false });
      if (!value) return;
      const { developmentState, taskDefinition } = value;

      if (!taskDefinition)
        return completeTasks({
          details: [],
          developmentState,
          taskDefinition: null,
          taskIDs,
        });
      const programs = [
        ...new Set(
          rows
            .filter(({ id }) => taskIDs.includes(id))
            .map(({ program }) => program),
        ),
      ];
      const { multiple, pool, roles, scope } = taskDefinition.assignment;
      if (scope === "assigned" || pool) {
        setRowSelectionModel([]);
        return completeTasks({
          details: [],
          developmentState,
          taskDefinition: developmentState ? null : taskDefinition.key,
          taskIDs,
        });
      }
      const userOptions = getUserOptions(users)({ programs, roles });
      if (multiple) {
        return openSelectUserGroupsDialog({
          developmentState,
          programs,
          roles,
          taskDefinition,
          taskIDs,
          users: userOptions,
        });
      } else {
        return openSelectUserDialog({
          developmentState,
          taskDefinition,
          taskIDs,
          users: userOptions,
        });
      }
    };

  const handleSelectionModelChange = useCallback((selection) => {
    setRowSelectionModel(selection);
  }, []);

  const onModelChange = useCallback(() => {
    page.current = 0;
    pagesToNextCursors.current = [];
    setPaginationModel({ ...paginationModel, page: 0 });
  }, [paginationModel]);

  const handleReadyFilterChange = useCallback(
    (_event, nextReadyFilter) => {
      if (!nextReadyFilter) return;
      onModelChange();
      setReadyFilter(nextReadyFilter);
    },
    [onModelChange],
  );

  const onPaginationModelChange = useCallback(
    (model) => {
      if (model.pageSize !== paginationModel.pageSize) {
        page.current = 0;
        pagesToNextCursors.current = [];
        setPaginationModel({ ...model, page: 0 });
      } else {
        page.current = model.page;
        setPaginationModel(model);
      }
    },
    [paginationModel],
  );

  const openCompareReferencePassageDialog = (row) => (_e) => {
    const passageID = row.passage?.id;
    setCompareReferencePassageDialogProps({
      closeOptions: ["close"],
      onClose: () => setCompareReferencePassageDialogProps({ open: false }),
      open: true,
      passageID,
    });
  };

  const openCompleteTasksDialog =
    (tasks = []) =>
    (selection = []) =>
    (row) =>
    (_e) => {
      const taskIDs = row ? [row.id] : selection;
      const selected = tasks.filter((task) => taskIDs.includes(task.id));
      const prerequisites =
        selected[0].taskDefinition.completion.prerequisites || [];
      if (
        (prerequisites.includes("noOutstandingTasks") &&
          selected.some((task) => task.outstandingTasks.length > 1)) ||
        (prerequisites.includes("requireQRR") &&
          !selected.every((task) => task.qrr)) ||
        (prerequisites.includes("resolveChangeTags") &&
          selected[0].taskDefinition.key !== "Final Review" &&
          selected.some(
            (task) =>
              task.item?.hasUnresolvedChangeTags ||
              task.passage?.hasUnresolvedChangeTags,
          ))
      ) {
        return openMissingRequirementsDialog({ prerequisites, selected });
      }
      setCompleteTasksDialogProps({
        ...selected[0].taskDefinition.completion,
        onClose: handleCompleteTasksDialogClose(
          taskIDs,
          selected,
          prerequisites,
        ),
        open: true,
        taskIDs,
        types: ["item", "passage"]
          .map((type) => (selected.some((task) => !!task[type]) ? type : null))
          .filter(Boolean),
      });
    };

  const getTarget = (task) => {
    const name =
      task.passage?.name || task.item?.passage?.name || task.item?.name;
    return name;
  };

  const openItem = (newWindow) => (task) => () => {
    const uri = getWorkspaceURIFromTask(task);
    const name = getTarget(task);
    if (newWindow) {
      window.open(uri, name);
    } else {
      navigate(uri, { reloadDocument: true });
    }
  };

  const openMissingRequirementsDialog = ({ prerequisites, selected }) => {
    setMissingRequirementsDialogProps({
      onClose: (uri) => {
        setMissingRequirementsDialogProps({ open: false });
        if (uri) navigate(uri);
      },
      open: true,
      prerequisites,
      tasks: selected,
      types: ["item", "passage"].filter((type) =>
        selected.some((task) => !!task[type]),
      ),
    });
  };

  const openPreviewContentDialog = (object) => (_e) => {
    setPreviewContentDialogProps({
      onClose: () => setPreviewContentDialogProps({ open: false }),
      open: true,
      object,
    });
  };

  const openSelectUserDialog = ({
    developmentState,
    taskDefinition,
    taskIDs,
    users,
  }) => {
    setSelectUserDialogProps({
      onClose: (user) => {
        setSelectUserDialogProps({ open: false });
        if (!user) return;
        setRowSelectionModel([]);
        completeTasks({
          details: [
            {
              assignedUsers: [user],
            },
          ],
          developmentState,
          taskDefinition: developmentState ? null : taskDefinition.key,
          taskIDs,
        });
      },
      open: true,
      users,
    });
  };

  const openSelectUserGroupsDialog = ({
    developmentState,
    programs,
    roles,
    taskDefinition,
    taskIDs,
    users,
  }) => {
    setSelectUserGroupsDialogProps({
      details: taskDefinition.creation.details,
      onClose: (details) => {
        setSelectUserGroupsDialogProps({ open: false });
        if (!details) return;
        setRowSelectionModel([]);
        completeTasks({
          details,
          developmentState,
          taskDefinition: developmentState ? null : taskDefinition.key,
          taskIDs,
        });
      },
      open: true,
      programs,
      roles,
      users,
    });
  };

  const isRowSelectable = useCallback(
    (tasks = []) =>
      (params) => {
        const row = params.row;
        return (
          rowSelectionModel.includes(params.id) ||
          (rowSelectionModel.every((taskID) => {
            const task = tasks.find(({ id }) => id === taskID);
            // A selected id that is not among `tasks` blocks selection.
            if (!task) return false;
            return (
              task.program === row.program &&
              _.isEqual(task.taskDefinition.completion)(
                row.taskDefinition.completion,
              ) &&
              task._actions.every((action) => row._actions.includes(action)) &&
              row._actions.every((action) => task._actions.includes(action))
            );
          }) &&
            !artTasksPending(row))
        );
      },
    [rowSelectionModel],
  );

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        flex: 1,
        headerName: "Task Name",
        renderCell: (params) => {
          const passageOnly =
            !params?.row?.item?.id && !!params?.row?.passage?.id;
          const uri = getWorkspaceURIFromTask(params.row);
          return (
            <>
              <Link
                component={RouterLink}
                disabled={!params?.row._actions.includes("edit")}
                reloadDocument
                to={uri}
                underline="always"
              >
                {params.value}
              </Link>
              <IconButton
                color="secondary"
                component={RouterLink}
                disabled={!params?.row?._actions.includes("edit")}
                size="small"
                target={getTarget(params?.row)}
                to={uri}
              >
                <OpenInNewIcon />
              </IconButton>
              {!params.row.taskDefinition.execution.permissions?.includes(
                "externalReview",
              ) && (
                <PreviewContent
                  objects={
                    passageOnly
                      ? [
                          {
                            id: params?.row?.passage?.id,
                            versionID: params?.row?.passage?.versionID,
                          },
                        ]
                      : [
                          {
                            id: params?.row?.item?.id,
                            versionID: params?.row?.item?.versionID,
                          },
                        ]
                  }
                  objectType={passageOnly ? "Passage" : "Item"}
                  previewTypes={PREVIEW_TYPES}
                />
              )}
            </>
          );
        },
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueOptions: taskNameOptions,
      },
      {
        field: "passageName",
        flex: 1,
        headerName: "Passage Name",
        minWidth: 150,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) =>
          row.passage?.name || row.item?.passage?.name || "",
      },
      {
        field: "itemName",
        flex: 1,
        headerName: "Item Name",
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) => row.item?.name || "",
      },
      {
        field: "contentArea",
        headerName: "Content Area",
        minWidth: 200,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) =>
          row.item?.contentArea || row.passage?.contentArea || "",
        valueOptions: contentAreaOptions,
      },
      {
        field: "description",
        flex: 2,
        headerName: "Description",
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "string",
        valueGetter: ({ row }) => row.item?.description || "",
      },
      {
        field: "programName",
        headerName: "Program",
        minWidth: 200,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) => {
          if (
            row.taskDefinition.execution.permissions?.includes("externalReview")
          )
            return "";
          const program = programs.find(
            ({ id }) => row.item?.program === id || row.passage?.program === id,
          );
          return program?.name || "";
        },
        valueOptions: programs.map(({ name }) => name),
      },
      {
        field: "developmentState",
        headerName: "Development State",
        minWidth: 200,
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "singleSelect",
        valueGetter: ({ row }) => {
          if (
            row.taskDefinition.execution.permissions?.includes("externalReview")
          )
            return "";
          return (
            row.item?.developmentState || row.passage?.developmentState || ""
          );
        },
        valueOptions: developmentStateOptions,
      },
      {
        field: "assignedDateTime",
        headerName: "Assigned",
        minWidth: 200,
        type: "date",
        valueGetter: ({ row }) =>
          row.assignedDateTime ? new Date(row.assignedDateTime) : "",
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        minWidth: 125,
        type: "date",
        valueGetter: ({ row }) =>
          row.dueDate ? new Date(dueDateTransform(row.dueDate)) : "",
      },
    ];
  }, [programs, taskNameOptions, contentAreaOptions, developmentStateOptions]);

  const dataGridProps = {
    actions: [
      {
        disabled: (row) => !row._actions?.includes("edit"),
        label: "Open",
        onClick: openItem(false),
      },
      {
        disabled: (row) => !row._actions?.includes("edit"),
        label: "Open in New Window",
        onClick: openItem(true),
      },
      {
        disabled: (row) => !row._actions?.includes("complete"),
        label: "Complete",
        showInMenu: false,
        icon: (row) =>
          artTasksPending(row) ? (
            <Tooltip title="Art tasks pending">
              <Assignment
                fontSize="small"
                color="disabled"
                pointerEvents="auto"
              />
            </Tooltip>
          ) : row.ready ? (
            <LabelImportant fontSize="small" color="success" />
          ) : (
            <LabelImportantOutlined
              fontSize="small"
              sx={{
                color: "#000",
                "& path": {
                  fill: "none",
                  stroke: "#000",
                  strokeWidth: 1.5,
                  vectorEffect: "non-scaling-stroke",
                },
              }}
            />
          ),
        onClick: openCompleteTasksDialog(rows)(rowSelectionModel),
      },
      {
        disabled: (row) => !["English"].includes(row.passage?.contentArea),
        filter: (row) => row._actions?.includes("dynamicPassage"),
        label: "Compare Reference Passage",
        onClick: openCompareReferencePassageDialog,
      },
      {
        label: "Preview",
        onClick: (row) =>
          openPreviewContentDialog(
            !row?.item?.id && !!row?.passage?.id
              ? {
                  id: row?.passage?.id,
                  type: "Passage",
                  versionID: row?.passage?.versionID,
                  permissions: row.taskDefinition?.execution?.permissions || [],
                }
              : {
                  id: row?.item?.id,
                  type: "Item",
                  versionID: row?.item?.versionID,
                  permissions: row.taskDefinition?.execution?.permissions || [],
                },
          ),
      },
    ],
    checkboxSelection: true,
    columns: columns.filter(
      ({ field }) =>
        !rows.length ||
        ((field !== "dueDate" || rows.some(({ dueDate }) => !!dueDate)) &&
          (!rows.every((task) =>
            task.taskDefinition.execution.permissions?.includes(
              "externalReview",
            ),
          ) ||
            !["developmentState", "programName"].includes(field))),
    ),
    datagridKey,
    disableRowSelectionOnClick: true,
    disableSelectAllCheckbox: true,
    error: error?.errorInfo?.message,
    filterMode: "server",
    filterModel: { items: [], logicOperator: "and" },
    getRowClassName: ({ row }) => (row.ready ? "ready" : ""),
    isRowSelectable: isRowSelectable(rows),
    onFilterModelChange: onModelChange,
    onPaginationModelChange,
    onRowSelectionModelChange: handleSelectionModelChange,
    onSortModelChange: onModelChange,
    paginationMode: "server",
    paginationModel,
    rowCount: data?.tasksByUser.rowCount || 0,
    rows,
    rowSelectionModel,
    sortModel: [
      {
        field: "assignedDateTime",
        sort: "asc",
      },
    ],
    sortingMode: "server",
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">My Tasks</Typography>
        <Box
          sx={{ alignItems: "center", display: "flex", flexDirection: "row" }}
        >
          {!!rowSelectionModel.length &&
            selectedRows.every((task) =>
              task._actions.includes("complete"),
            ) && (
              <Button
                onClick={openCompleteTasksDialog(rows)(rowSelectionModel)()}
                size="small"
                startIcon={<CheckAllIcon />}
                sx={{ marginLeft: 2 }}
                variant="outlined"
              >
                Complete Selected Tasks
              </Button>
            )}
          <Typography
            id="task-filter-label"
            sx={{ marginLeft: 2 }}
            variant="subtitle2"
          >
            Task Filter
          </Typography>
          <ToggleButtonGroup
            aria-labelledby="task-filter-label"
            exclusive
            onChange={handleReadyFilterChange}
            size="small"
            sx={{ marginLeft: 1 }}
            value={readyFilter}
          >
            {READY_FILTER_OPTIONS.map(({ label, value }) => (
              <ToggleButton key={value} value={value}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      {fetching && <Backdrop loading={fetching} />}
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginX: 2,
          marginY: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <DataGrid {...dataGridProps}></DataGrid>
        </Box>
      </Paper>
      {compareReferencePassageDialogProps.open && (
        <CompareReferencePassageDialog
          {...compareReferencePassageDialogProps}
        />
      )}
      {completeTasksDialogProps.open && (
        <CompleteTasksDialog {...completeTasksDialogProps} />
      )}
      {missingRequirementsDialogProps.open && (
        <MissingRequirementsDialog {...missingRequirementsDialogProps} />
      )}
      {previewContentDialogProps.open && (
        <PreviewContentDialog {...previewContentDialogProps} />
      )}
      {selectUserDialogProps.open && (
        <SelectUserDialog {...selectUserDialogProps} />
      )}
      {selectUserGroupsDialogProps.open && (
        <SelectUserGroupsDialog {...selectUserGroupsDialogProps} />
      )}
    </>
  );
};

const artTasksPending = (row) =>
  row.taskDefinition.completion?.prerequisites?.includes(
    "noOutstandingTasks",
  ) && row.outstandingTasks.length > 1;

const completeTasksMutation = `
  mutation CompleteTasks($details: [TaskCompletionDetails!], $developmentState: String, $taskDefinition: String, $taskIDs: [ID!]!) {
    completeTasks(details: $details, developmentState: $developmentState, taskDefinition: $taskDefinition, taskIDs: $taskIDs) {
      completedDateTime
      completedByUser
      id
    }
  }`;

const getTasksByUserQuery = `
  query GetTasksByUser($filterModel: DataGridFilterModelInput!, $paginationModel: DataGridPaginationModelInput!, $readyFilter: TaskReadyFilter, $sortModel: [DataGridSortingItemInput!]) {
    tasksByUser(filterModel: $filterModel, paginationModel: $paginationModel, readyFilter: $readyFilter, sortModel: $sortModel) {
      pageInfo {
        hasNextPage
        nextCursor
        truncated
      }
      rowCount
      rows {
        _actions
        assignedDateTime
        dueDate
        id
        item {
          contentArea
          description
          developmentState
          hasUnresolvedChangeTags
          id
          name
          passage {
            id
            name
          }
          program
          versionID
        }
        name
        outstandingTasks
        passage {
          contentArea
          developmentState
          hasUnresolvedChangeTags
          id
          name
          program
          versionID
        }
        program
        qrr
        ready
        taskDefinition {
          completion {
            options {
              buttonLabel
              developmentState
              taskDefinition {
                assignment {
                  multiple
                  pool
                  roles
                  scope
                }
                creation {
                  details
                }
                key
              }
            }
            prerequisites
          }
          execution {
            application
            permissions
          }
          key
        }
      }
    }
  }`;

export { UserTasks, getTasksByUserQuery };
